import { useEffect, useState, useRef, useContext } from "react";
import ToolbarTop from "./Toolbar";
import { Field as BaseField } from "@base-ui-components/react/field";
import { interpreter_store_attributes } from "../../api/CallViewApi";
import { Fieldset } from "@base-ui-components/react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Select } from "@base-ui-components/react/select";
import { Separator } from "../ui/separator";
import { GlobalContext } from "../../App";
import { get_attributes_by_call_interpreter } from "../../api/InterpreterApi";
import { CheckIcon, ChevronsUpDown, ChevronsUpDownIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";

export default function CallView({
  callInfo,
  setFormSubmitted,
  setCallEvent,
  setCallInfo,
  callEvent,
  user,
}) {
  const { setToastOpen, setToastMessage, setToastSeverity } =
    useContext(GlobalContext);
  const [loading, setLoading] = useState(false);
  const [customerAttributes, setCustomerAttributes] = useState([]);
  const [formValues, setFormValues] = useState({ attrs: {} });
  const [language, setLanguage] = useState("");
  const [openSections, setOpenSections] = useState({
    customer: false,
    interpreter: false,
    language: false,
  });
  const [attrPopoverOpen, setAttrPopoverOpen] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmittedForEndedCall, setHasSubmittedForEndedCall] =
    useState(false);

  const handleFormSubmit = async (values) => {
    console.log("handleFormSubmit called with values:", values);
    try {
      const engagementId = callInfo?.engagement_id;
      if (!engagementId) throw new Error("Missing engagement ID");

      // Filter only inputs
      const filteredAttrs = {};
      customerAttributes.forEach((attr) => {
        if (attr.is_input === 1) {
          // Use the value from formValues or an empty string if not found
          const value = values.attrs[attr.attribute_id] || "";
          filteredAttrs[attr.attribute_id] = value;
        }
      });

      const payload = {
        attrs: filteredAttrs,
        language: language || "",
        category_code_name: callInfo.category_code_name || "",
        category_code_value: callInfo.category_code_value || "",
      };

      const response = await interpreter_store_attributes(
        payload,
        engagementId,
        callInfo.category_code_name || "",
        callInfo.category_code_value || "",
        language || ""
      );

      setToastOpen(true);
      setToastSeverity("success");
      setToastMessage("Form submitted successfully!");

      if (callEvent === "ENDED" || callEvent === "IDLE") {
        setFormSubmitted(true);
      }
    } catch (error) {
      console.error(error);
      setToastOpen(true);
      setToastSeverity("error");
      setToastMessage(
        "Failed to submit form: " + (error.message || "Unknown error")
      );
    }
  };

  const handleCallInfo = async (engagement_id) => {
    setFormSubmitted(false);
    try {
      const response = await get_attributes_by_call_interpreter(engagement_id);
      setCustomerAttributes(response.data.attributes);
      const languageValue = response.data.language || "";
      setLanguage(languageValue);
      const updated = {
        ...callInfo,
        ...response.data,
        category_code_name:
          response.data.category_code_name || callInfo.category_code_name || "",
        category_code_value:
          response.data.category_code_value ||
          callInfo.category_code_value ||
          "",
        category_code_values:
          response.data.category_code_values ||
          callInfo.category_code_values ||
          [],
        caller_number: response.data.caller_number || "",
        flow_name: response.data.flow_name || callInfo.flow_name || "",
        is_changed_by_agent: false,
      };
      setCallInfo(updated);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (callInfo && callInfo.engagement_id) {
      // Reset all form state when engagement_id changes (new call)
      setHasSubmittedForEndedCall(false);
      handleCallInfo(callInfo.engagement_id);
    }
  }, [callInfo?.engagement_id]);

  useEffect(() => {
    if (customerAttributes.length > 0) {
      // Only initialize form values if they haven't been set yet
      // This preserves user input after form submission
      setFormValues((prevValues) => {
        if (Object.keys(prevValues.attrs).length === 0) {
          // First time initialization
          const initialAttrs = {};
          customerAttributes.forEach((data) => {
            initialAttrs[data.attribute_id] = data.attribute_value || "";
          });
          return { attrs: initialAttrs };
        }
        // Preserve existing form values
        return prevValues;
      });
    }
  }, [customerAttributes]);

  useEffect(() => {
    if (callEvent === "ENDED" && !hasSubmittedForEndedCall) {
      console.log("Auto-submitting form due to call end");

      handleFormSubmit(formValues);
      setHasSubmittedForEndedCall(true);
    }
  }, [callEvent, hasSubmittedForEndedCall]);

  return (
    <>
      <ToolbarTop callInfo={callInfo} />
      <div>
        <Accordion type="multiple" className="w-full" defaultValue={[]}>
          <AccordionItem value="item-1">
            <AccordionTrigger>Notes</AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4 text-balance">
              <div dangerouslySetInnerHTML={{ __html: callInfo?.notes }} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-2">
            <AccordionTrigger>Script</AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4 text-balance">
              <div dangerouslySetInnerHTML={{ __html: callInfo?.script }} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          handleFormSubmit(formValues);
        }}
        className="flex flex-col"
      >
        <div className="flex flex-1 flex-col gap-10 mt-4">
          <div className="flex flex-col">
            <label className="text-sm text-muted-foreground mb-1">
              Language
            </label>

            <Button
              disabled
              variant="outline"
              role="combobox"
              aria-expanded={openSections.language}
              className="justify-between cursor-pointer"
            >
              <span className="line-clamp-1">
                {language ? language : "Language not selected"}
              </span>
              <ChevronsUpDown className="opacity-50" />
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-10">
            <BaseField.Root className="flex flex-col gap-1">
              <BaseField.Label className="text-sm font-medium text-gray-900">
                {callInfo?.category_code_name}
              </BaseField.Label>
              <div className="flex flex-col gap-2 items-start w-full">
                {callInfo?.category_code_name &&
                callInfo?.category_code_name != "null" &&
                Array.isArray(callInfo?.category_code_values) &&
                callInfo.category_code_values.length > 0 ? (
                  <Select.Root
                    value={callInfo?.category_code_value || ""}
                    onValueChange={(value) =>
                      setCallInfo({
                        ...callInfo,
                        category_code_value: value,
                        is_changed_by_agent: true,
                      })
                    }
                    className="w-full"
                    disabled={
                      callInfo?.category_code_value != "" &&
                      callInfo?.category_code_value != null &&
                      !callInfo?.is_changed_by_agent
                    }
                    defaultValue={callInfo?.category_code_value || ""}
                  >
                    <Select.Trigger
                      className={`h-10 w-full rounded-md border border-gray-200 pl-3.5 pr-2 text-base text-gray-900 focus:outline-2 focus:-outline-offset-1 focus:outline-blue-800 flex items-center justify-between ${
                        callInfo?.category_code_value != "" &&
                        callInfo?.category_code_value != null &&
                        !callInfo?.is_changed_by_agent
                          ? "cursor-not-allowed opacity-50"
                          : ""
                      }`}
                    >
                      <Select.Value
                        placeholder={
                          callInfo?.category_code_value || "Select a value"
                        }
                      />
                      <Select.Icon className="flex">
                        <ChevronsUpDownIcon />
                      </Select.Icon>
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Positioner
                        className="outline-none"
                        sideOffset={8}
                      >
                        <Select.ScrollUpArrow className="top-0 z-[1] flex h-4 w-full cursor-default items-center justify-center rounded-md bg-[canvas] text-center text-xs before:absolute before:top-[-100%] before:left-0 before:h-full before:w-full before:content-[''] data-[direction=down]:bottom-0 data-[direction=down]:before:bottom-[-100%]" />
                        <Select.Popup className="group [max-height:var(--available-height)] origin-[var(--transform-origin)] overflow-y-auto rounded-md bg-[canvas] py-1 text-gray-900 shadow-lg shadow-gray-200 outline-1 outline-gray-200 transition-[transform,scale,opacity]  data-[ending-style]:scale-100 data-[ending-style]:opacity-100 data-[ending-style]:transition-none data-[starting-style]:scale-90 data-[starting-style]:opacity-0 data-[starting-style]:transition-none data-[side=none]:data-[starting-style]:scale-100 data-[side=none]:data-[starting-style]:opacity-100 data-[side=none]:data-[starting-style]:transition-none">
                          {callInfo.category_code_values.map((value) => (
                            <Select.Item
                              className="grid min-w-[var(--anchor-width)] cursor-default grid-cols-[0.75rem_1fr] items-center gap-2 py-2 pr-4 pl-2.5 text-sm leading-4 outline-none select-none group-data-[side=none]:min-w-[calc(var(--anchor-width)+1rem)] group-data-[side=none]:pr-12 group-data-[side=none]:text-base group-data-[side=none]:leading-4 data-[highlighted]:relative data-[highlighted]:z-0 data-[highlighted]:text-gray-50 data-[highlighted]:before:absolute data-[highlighted]:before:inset-x-1 data-[highlighted]:before:inset-y-0 data-[highlighted]:before:z-[-1] data-[highlighted]:before:rounded-sm data-[highlighted]:before:bg-gray-900"
                              value={value}
                              key={value}
                            >
                              <Select.ItemIndicator className="col-start-1">
                                <CheckIcon className="size-3" />
                              </Select.ItemIndicator>
                              <Select.ItemText className="col-start-2">
                                {value}
                              </Select.ItemText>
                            </Select.Item>
                          ))}
                        </Select.Popup>
                        <Select.ScrollDownArrow className="bottom-0 z-[1] flex h-4 w-full cursor-default items-center justify-center rounded-md bg-[canvas] text-center text-xs before:absolute before:top-[-100%] before:left-0 before:h-full before:w-full before:content-[''] data-[direction=down]:bottom-0 data-[direction=down]:before:bottom-[-100%]" />
                      </Select.Positioner>
                    </Select.Portal>
                  </Select.Root>
                ) : callInfo?.category_code_name &&
                  callInfo?.category_code_name != "null" &&
                  (!Array.isArray(callInfo?.category_code_values) ||
                    callInfo.category_code_values.length === 0) ? (
                  <BaseField.Control
                    value={callInfo?.category_code_value || ""}
                    onChange={(e) =>
                      setCallInfo({
                        ...callInfo,
                        category_code_value: e.target.value,
                      })
                    }
                    className="h-10 w-full rounded-md border border-gray-200 pl-3.5 text-base text-gray-900 focus:outline-2 focus:-outline-offset-1 focus:outline-blue-800"
                  />
                ) : null}
              </div>
            </BaseField.Root>
          </div>

          <Separator className="mt-5" />

          {customerAttributes.length > 0 && (
            <Fieldset.Root className="flex flex-col gap-4">
              <Fieldset.Legend className="mb-2 text-lg font-medium text-gray-900">
                {customerAttributes[0].customer_name}
              </Fieldset.Legend>
              <div className="grid grid-cols-1 gap-10">
                {customerAttributes.map((data) =>
                  data.is_input ? (
                    <BaseField.Root
                      key={data.attribute_id}
                      className="flex flex-col gap-1"
                    >
                      <BaseField.Label className="text-sm font-medium text-gray-900">
                        {data.attribute_label || data.attribute_name}
                        {data.is_required === 1 && (
                          <span className="text-red-500">*</span>
                        )}
                      </BaseField.Label>
                      {data.attribute_type === "select" &&
                        (() => {
                          const raw = data.attribute_options;
                          const optionsArray = Array.isArray(raw)
                            ? raw
                            : typeof raw === "string" && raw.length > 0
                            ? raw
                                .split(",")
                                .map((s) => s.trim())
                                .filter((s) => s.length > 0)
                            : [];
                          return optionsArray.length > 0 ? (
                            <Popover
                              open={
                                attrPopoverOpen[`attr-${data.attribute_id}`] ||
                                false
                              }
                              onOpenChange={(open) =>
                                setAttrPopoverOpen((prev) => ({
                                  ...prev,
                                  [`attr-${data.attribute_id}`]: open,
                                }))
                              }
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  aria-expanded={
                                    attrPopoverOpen[`attr-${data.attribute_id}`]
                                  }
                                  className="w-full justify-between"
                                >
                                  <span className="line-clamp-1">
                                    {formValues.attrs[data.attribute_id] ||
                                      "Select value..."}
                                  </span>
                                  <ChevronsUpDown className="opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[300px] p-0">
                                <Command>
                                  <CommandInput placeholder="Search value..." />
                                  <CommandList>
                                    <CommandEmpty>
                                      No values found.
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {optionsArray.map((opt) => (
                                        <CommandItem
                                          key={opt}
                                          value={opt}
                                          onSelect={() => {
                                            setFormValues({
                                              attrs: {
                                                ...formValues.attrs,
                                                [data.attribute_id]: opt,
                                              },
                                            });
                                            setAttrPopoverOpen((prev) => ({
                                              ...prev,
                                              [`attr-${data.attribute_id}`]: false,
                                            }));
                                          }}
                                          className="cursor-pointer"
                                        >
                                          {opt}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          ) : null;
                        })()}
                      {!(data.attribute_type === "select") && (
                        <BaseField.Control
                          value={formValues.attrs[data.attribute_id] || ""}
                          onChange={(e) =>
                            setFormValues({
                              attrs: {
                                ...formValues.attrs,
                                [data.attribute_id]: e.target.value,
                              },
                            })
                          }
                          placeholder={
                            data.is_required === 1 ? "Required" : undefined
                          }
                          className="h-10 w-full rounded-md border border-gray-200 px-3.5"
                        />
                      )}
                    </BaseField.Root>
                  ) : (
                    <div key={data.attribute_id} className="flex flex-col">
                      <h6>{data.attribute_name}</h6>
                      <p className="text-xs font-light text-gray-500">
                        {data.attribute_subtitle}
                      </p>
                    </div>
                  )
                )}
              </div>
            </Fieldset.Root>
          )}
        </div>

        <div className="mt-6 flex justify-start">
          <button
            type="submit"
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 hover:bg-gray-50"
          >
            Submit
          </button>
        </div>
      </form>
    </>
  );
}
